/**
 * Admin API client.
 *
 * Thin fetch wrapper for /api/admin/* endpoints with JWT bearer auth.
 * Token getter is injected after auth initializes to avoid circular deps.
 */

let _baseUrl = '/api/admin';
let _getToken: (() => Promise<string | null>) | null = null;

export function initAdminApiClient(baseUrl: string): void {
  _baseUrl = baseUrl.replace(/\/+$/, '');
}

export function setTokenGetter(fn: () => Promise<string | null>): void {
  _getToken = fn;
}

export class AdminApiError extends Error {
  constructor(
    public status: number,
    public detail: string,
  ) {
    super(detail);
    this.name = 'AdminApiError';
  }
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  opts?: { skipAuth?: boolean; includeCredentials?: boolean },
): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  if (!opts?.skipAuth && _getToken) {
    const token = await _getToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
  }

  const url = `${_baseUrl}${path}`;
  const response = await fetch(url, {
    method,
    headers,
    body: body != null ? JSON.stringify(body) : undefined,
    credentials: opts?.includeCredentials ? 'include' : 'same-origin',
  });

  if (!response.ok) {
    let detail = response.statusText;
    try {
      const errBody = await response.json();
      detail = errBody.detail || detail;
    } catch { /* ignore */ }
    throw new AdminApiError(response.status, detail);
  }

  if (response.status === 204) return undefined as T;
  return response.json();
}

// ── Auth endpoints (no token required) ──────────────────────────────

export interface TokenResponse {
  access_token: string;
  expires_at: number;
  user: { id: string; email: string; display_name: string | null };
}

export function requestOtp(email: string): Promise<{ message: string }> {
  return request('POST', '/auth/otp/request', { email }, { skipAuth: true });
}

export function verifyOtp(email: string, code: string): Promise<TokenResponse> {
  return request('POST', '/auth/otp/verify', { email, code }, {
    skipAuth: true,
    includeCredentials: true,
  });
}

export function exchangeGoogleToken(id_token: string): Promise<TokenResponse> {
  return request('POST', '/auth/google/exchange', { id_token }, {
    skipAuth: true,
    includeCredentials: true,
  });
}

export function refreshTokens(): Promise<TokenResponse> {
  return request('POST', '/auth/refresh', undefined, {
    skipAuth: true,
    includeCredentials: true,
  });
}

export function logout(): Promise<void> {
  return request('POST', '/auth/logout', undefined, {
    skipAuth: true,
    includeCredentials: true,
  });
}

// ── Workspace endpoints ─────────────────────────────────────────────

export interface WorkspaceSummary {
  workspace_key: string;
  public_workspace_id: string;
  display_name: string;
  role: string;
  capabilities: string[];
}

export interface WorkspaceDetail {
  workspace_key: string;
  public_workspace_id: string;
  display_name: string;
  tenant_id: string;
  role: string;
  capabilities: string[];
  admin_experience_type: string;
  transcript_policy: string;
  primary_region: string;
}

export interface MetricPoint {
  metric_name: string;
  value: number;
  timestamp: string;
  dimension: string | null;
}

export interface WorkspaceOverview {
  workspace_key: string;
  metrics: MetricPoint[];
}

export interface WorkspacePerformance {
  workspace_key: string;
  metrics: MetricPoint[];
}

export interface ConversationSummary {
  conversation_id: string;
  user_id_hash: string;
  started_at: string;
  last_message_at: string;
  turn_count: number;
  routing_tier: string | null;
  intent_summary: string | null;
}

export interface ConversationIntentSummary {
  intent: string;
  count: number;
}

export interface ConversationTurn {
  timestamp: string | null;
  user_message: string | null;
  assistant_message: string | null;
  model_used: string | null;
  response_depth: string | null;
  tokens_used: number | null;
}

export interface ConversationDetail extends ConversationSummary {
  turns: ConversationTurn[];
}

export interface ConversationListResponse {
  workspace_key: string;
  conversations: ConversationSummary[];
  total: number;
  top_intents: ConversationIntentSummary[];
}

export interface MemberResponse {
  user_id: string;
  email: string;
  display_name: string | null;
  role: string;
  joined_at: string;
}

export interface InviteResponse {
  id: string;
  email: string;
  role: string;
  invited_by: string;
  created_at: string;
  expires_at: string;
  accepted_at: string | null;
}

export interface MembersListResponse {
  workspace_key: string;
  members: MemberResponse[];
  pending_invites: InviteResponse[];
}

export function listWorkspaces(): Promise<WorkspaceSummary[]> {
  return request('GET', '/me/workspaces');
}

export function getWorkspace(key: string): Promise<WorkspaceDetail> {
  return request('GET', `/workspaces/${encodeURIComponent(key)}`);
}

export function getOverview(key: string): Promise<WorkspaceOverview> {
  return request('GET', `/workspaces/${encodeURIComponent(key)}/overview`);
}

export function getPerformance(
  key: string,
  window: '48h' | '7d' | '30d' = '48h',
): Promise<WorkspacePerformance> {
  return request('GET', `/workspaces/${encodeURIComponent(key)}/performance?window=${window}`);
}

export function listConversations(
  key: string,
  limit = 50,
  offset = 0,
  windowDays: 7 | 30 = 7,
): Promise<ConversationListResponse> {
  return request(
    'GET',
    `/workspaces/${encodeURIComponent(key)}/conversations?limit=${limit}&offset=${offset}&window_days=${windowDays}`,
  );
}

export function getConversation(
  key: string,
  conversationId: string,
): Promise<ConversationDetail> {
  return request(
    'GET',
    `/workspaces/${encodeURIComponent(key)}/conversations/${encodeURIComponent(conversationId)}`,
  );
}

export function listMembers(key: string): Promise<MembersListResponse> {
  return request('GET', `/workspaces/${encodeURIComponent(key)}/members`);
}

export function inviteMember(
  key: string,
  email: string,
  role: string,
): Promise<InviteResponse> {
  return request('POST', `/workspaces/${encodeURIComponent(key)}/members/invite`, {
    email,
    role,
  });
}
